import {tableFromIPC, Table, tableToIPC} from 'apache-arrow'
import {ColumnTable} from "arquero";
import * as aq from 'arquero';
import * as duckdb from '@duckdb/duckdb-wasm';
import {ClrDatagridComparatorInterface, ClrDatagridStateInterface} from "@clr/angular";
import {NetscopeUtils} from "@app/netscope/netscope-utils";
import {FlowsRepository, NetworkViewerData, ProtocolAnalysisSelection} from "@app/netscope/model/flows-repository";
import {EXTERNAL_IP_UUID_PREFIX, IP_NETWORK_UUID_PREFIX, uuidAddress} from "@app/netscope/model/ip-addresses";

export class NetworkLevelFlow {
  srcNetworkAddress: string;
  dstNetworkAddress: string;
  exchangedBytes: number;
  displayedValue: number;
  formattedExchangedBytes: string;
  flows: NetworkViewerData[] = [];
}

export class AsnEntry {
  asn: string;
  asnNum: number;
}

export class CountryEntry {
  country: string;
  countryCode: string;
}

export class FlowRessourceObject {
  type: string;
  uuid: string;
  name: string;
  host?: FlowRessourceObject;
  vms?: FlowRessourceObject[];
  address: string;
  resolvedDomain?: DomainResolution;
  // UI properties that are used by renderers of the CommonFlowView components
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
  minAngle?: number;
  maxAngle?: number;
  angle?: number;
  fx?: number;
  fy?: number;
  linefx?: number;
  linefy?: number;
  visible?: boolean;
  countryInfo?: CountryEntry;
  country_code?: string;
  asnInfo?: AsnEntry;
  asnNum?: number;
  nonInteractive: boolean = false;
  // For groups
  parent?: FlowRessourceObject;
  subnodes?: FlowRessourceObject[] = [];
  subnodesMap?: Map<string, FlowRessourceObject> = new Map();
  // For apps groups
  appsGroupObject?: AppsGroups
  // For an apps group drawn as a box inside another one that includes it
  inclusion?: IncludedAppsGroupBoxState;
  // Editable apps groups
  membersDisplayMode: AppsGroupMembersDisplayMode = "summarized";
}

/**
 * How much of an apps group is displayed, each level showing what the previous one does and more. A mode is not
 * applied as such by the renderer: it is a preset over the display choices of the group (the visibility of its
 * members, the boxes of the apps groups it includes, its inside traffic), applied once when the user picks it,
 * and which the user is then free to tune one by one.
 *
 *  - 'summarized': everything is folded into the node of the apps group, included apps groups as well,
 *  - 'sub_groups': the apps groups it includes are shown as boxes of their own, the members staying folded,
 *  - 'servers': the members exposing a port in a drawn flow are shown as nodes of their own,
 *  - 'servers_and_clients': every member taking part in a drawn flow is shown as a node of its own,
 *  - 'all': every member of the group is shown, including those that exchanged nothing,
 *  - 'all_traffic': every member, and the flows inside the apps group drawn as well.
 *
 * The flows inside an apps group are only drawn at the last level: below it, 'exposing a port' and 'taking
 * part in a flow' are therefore about the flows exchanged with the other apps groups.
 */
export type AppsGroupMembersDisplayMode = "summarized" | "sub_groups" | "servers" | "servers_and_clients" | "all" | "all_traffic";

/**
 * Mode of an apps group the user made no choice on: the apps groups it includes are shown as boxes, which is
 * what an apps group holding others is about, while any other apps group is summarized.
 */
export function defaultDisplayMode(appGroup: { included_apps_groups?: { name: string }[] }): AppsGroupMembersDisplayMode {
  return (appGroup?.included_apps_groups ?? []).length > 0 ? "sub_groups" : "summarized";
}

/**
 * Roles played by a resource in the flows of the displayed period. Both can be true at once, as a server
 * answering on its port can also initiate flows towards other servers.
 */
export class MemberRoles {
  server: boolean = false;
  client: boolean = false;
}

/**
 * Whether a member of an apps group is shown as a node of its own under a given display mode. A member with
 * no known role took part in no flow at all: only the 'all' mode shows it.
 */
export function memberIsVisibleInMode(roles: MemberRoles, mode: AppsGroupMembersDisplayMode): boolean {
  if (mode == "all" || mode == "all_traffic") {
    return true;
  }
  if (roles == undefined) {
    return false;
  }
  if (mode == "servers") {
    return roles.server;
  }
  if (mode == "servers_and_clients") {
    return roles.server || roles.client;
  }
  return false;
}

export class FlowMetric {
  exchanged_bytes: number;
  exchanged_packets: number;
  // transport_protocol: string;
  transport_protocols: string[];
  application_protocol: string;
  port: number;
  direction: number;
}

export class Threat {
  ip: string;
  port: number;
  url: string;
  threat: string;
  categories: string[];
  tags: string[];
  link: string;
  score: number;
  risk_level: string;
  origin: string;
  threat_dict: Map<string, string[]>;
  // ip_info: IpInfo
}

export class ThreatResponse {
  date: number;
  flow: FlowDetail;
  threat: Threat;
  score: number;
  info_ip: IpDescription;
}

export class FlowDetail {
  src_ipaddr: string;
  source: FlowRessourceObject;
  src_ip_resolved: string;
  src_ip_resolved_class: string;
  src_resolve_obj: DomainResolution;
  dst_ipaddr: string;
  target: FlowRessourceObject;
  dst_ip_resolved: string;
  dst_ip_resolved_class: string;
  dst_resolve_obj: DomainResolution;
  exchanged_bytes: number;
  exchanged_packets: number;
  metrics: Map<number, FlowMetric> = new Map<number, FlowMetric>();
  port: number;
  direction: number;
  protocol: string;
  transport_protocols: any[];
  date: number;
  threat: Threat;
  showActions: boolean = true;
  showDependencyViewerIcon: boolean = true;
  showClustersViewerIcon: boolean = true;
  showHeatmapViewerIcon: boolean = true;
  // x1?: number;
  // y1?: number;
  // x2?: number;
  // y2?: number;
  temporalParameter?: TemporalParameter;
  // For grouping of nodes in clusters-viewer
  shouldBeDeleted?: boolean = false;
  subflows?: FlowDetail[] = [];

  static defaultCsvHeader = () : string => {
    return `SourceIp,SourceName,DestinationIp,DestinationName,ExchangedBytes,ExchangedPackets`
  }

  static defaultCsvHeaderWithPorts = () : string => {
    return `SourceIp,SourceName,DestinationIp,DestinationName,Port,Protocol,ExchangedBytes,ExchangedPackets`
  }

  getExchangedBytes = () : number => {
    if (this.subflows.length == 0) {
      return this.exchanged_bytes;
    } else {
      let result = 0;
      for (let subflow of this.subflows) {
        result += subflow.getExchangedBytes();
      }
      return result;
    }
  }

  exportAsCsvRow = () : string => {
    return `${this.src_ipaddr},${this.source?.name},${this.dst_ipaddr},${this.target?.name},${this.exchanged_bytes},${this.exchanged_packets}\n`
  }

  exportAsCsvRowWithPorts = () : string => {
    let result = "";
    for (let [portNumber, metric] of this.metrics) {
      result += `${this.src_ipaddr},${this.source?.name},${this.dst_ipaddr},${this.target?.name},${metric.port},${metric.application_protocol},${metric.exchanged_bytes},${metric.exchanged_packets}\n`
    }
    return result
  }

  clone = () : FlowDetail => {
    let result: FlowDetail = new FlowDetail();

    result.src_ipaddr = this.src_ipaddr;
    result.source = this.source;
    result.src_ip_resolved = this.src_ip_resolved;
    result.src_ip_resolved_class = this.src_ip_resolved_class;
    result.src_resolve_obj = this.src_resolve_obj;
    result.dst_ipaddr = this.dst_ipaddr;
    result.target = this.target;
    result.dst_ip_resolved = this.dst_ip_resolved;
    result.dst_ip_resolved_class = this.dst_ip_resolved_class;
    result.dst_resolve_obj = this.dst_resolve_obj;
    result.exchanged_bytes = this.exchanged_bytes;
    result.exchanged_packets = this.exchanged_packets;
    result.metrics = structuredClone(this.metrics);
    result.port = this.port;
    result.protocol = this.protocol;
    result.transport_protocols = this.transport_protocols;
    result.date = this.date;
    result.threat = this.threat;
    result.showActions = this.showActions;
    result.showDependencyViewerIcon = this.showDependencyViewerIcon;
    result.showClustersViewerIcon = this.showClustersViewerIcon;
    result.showHeatmapViewerIcon = this.showHeatmapViewerIcon;
    // result.x1 = this.x1;
    // result.y1 = this.y1;
    // result.x2 = this.x2;
    // result.y2 = this.y2;
    result.temporalParameter = this.temporalParameter;

    return result;
  }

}

export class GraphDataObject {
  flowsRepository: FlowsRepository;
  links: FlowDetail[] = [];
  nodes: FlowRessourceObject[] = [];
}

export class ProtocolAnalysisRendererNode {
  svg_id: string;
  svg_label: string;
  uuid: string;
  name: string;
  parent: object;
  role: string;
  subSelection: ProtocolAnalysisSelection;
}

export class EnrichedWithProtocolAnalysisRendererNode {
  object: ProtocolAnalysisRendererNode = new ProtocolAnalysisRendererNode();
  parent: EnrichedWithProtocolAnalysisRendererNode;
  flows: FlowDetail[] = [];
  _currentSize: number | undefined;
  getChildren(): EnrichedWithProtocolAnalysisRendererNode[] {
    return [];
  }
  public get children() {
    return this.getChildren();
  }
  public get size(): number {
    // Memoize the size, to prevent many recomputation
    if (this._currentSize == undefined) {
      this._currentSize = this.getChildren().map((child) => child.size).reduce((total, newValue) => total + newValue, 0);
    }
    return this._currentSize;
  }
}

export class ResourceProtocolData extends EnrichedWithProtocolAnalysisRendererNode{
  resource: FlowRessourceObject;
  totalBytes: number = 0;

  override getChildren(): EnrichedWithProtocolAnalysisRendererNode[] {
    return [];
  }

  public override get size() {
    return this.totalBytes;
  }
}

export class SourceResourceProtocolData extends ResourceProtocolData {
  destinations: Map<string, ResourceProtocolData> = new Map();
  override getChildren(): EnrichedWithProtocolAnalysisRendererNode[] {
    return Array.from(this.destinations.values());
  }
}

export class VirtualMachineWithAppsGroups {
  name: string;
  uuid: string;
  apps_groups: any[];
  type = "vm";

  constructor(uuid, name, apps_groups) {
    this.uuid = uuid;
    this.name = name;
    this.apps_groups = apps_groups;
  }
}

export class GenericTopologyTreeNode {
  type: string;
  name: string;
}

export class GenericTopologyTreeNodeWithUuid extends GenericTopologyTreeNode {
  uuid: string;
}

export class VirtualMachineTreeNode extends GenericTopologyTreeNodeWithUuid {
}

export class HostSystemTreeNode extends GenericTopologyTreeNodeWithUuid {
}

export class GenericNetworkResourceTopologyTreeNode extends GenericTopologyTreeNodeWithUuid {
}

export class NetworkTopologyTreeNode extends GenericNetworkResourceTopologyTreeNode {
  children: Array<VirtualMachineTreeNode | HostSystemTreeNode>;
  vms: Array<VirtualMachineTreeNode>;
  hosts: Array<HostSystemTreeNode>;
}

export class DistributedVSwitchTreeNode extends GenericNetworkResourceTopologyTreeNode {
  children: Array<DistributedPortgroupTreeNode>;
  portgroups: Array<DistributedPortgroupTreeNode>;
}

export class DistributedPortgroupTreeNode extends GenericNetworkResourceTopologyTreeNode {
  uplink: boolean;
  children: Array<HostSystemTreeNode>;
}

export class DatacenterTopologyTreeNode extends GenericTopologyTreeNode {
  children: Array<NetworkTopologyTreeNode | DistributedVSwitchTreeNode>;
}

export class DCNetscopeResource {
  uuid: string;

  constructor(uuid: string) {
    this.uuid = uuid;
  }

  get key(): string {
    return `${this.uuid}`;
  }
}

export class DCNetscopeResourceWithName extends DCNetscopeResource {
  name: string;
  type?: string;
  address?: string;

  constructor(uuid: string, name: string) {
    super(uuid);
    this.name = name;
    this._detectTypeAndAddressIfRelevant();
  }

  _detectTypeAndAddressIfRelevant() {

    let mapType: Map<string, string> = new Map(Object.entries({
      "vim.UnknownIpaddress": "unknown_ip",
      "vim.VirtualMachine": "vm",
      "vim.HostSystem": "host",
      "xo.VirtualMachine": "vm",
      "xo.HostSystem": "host",
      "nu.VirtualMachine": "vm",
      "nu.HostSystem": "host",
    }));

    let uuidParts = this.uuid.split(":");
    if (uuidParts.length >= 2) {
      let longType = uuidParts[0];
      let shortUuid = uuidParts[1];
      if (mapType.has(longType)) {
        this.type = mapType.get(longType);
        this.address = shortUuid;
      }
    }
  }

  override get key(): string {
    return `${this.name}`;
  }
}

export class UserInfo {
  email: string;
  email_verified: boolean;
  family_name: string;
  given_name: string;
  name: string;
  preferred_username: string;
}

export class TelemetryInfo {
  application: string;
  build: string;
  expdate: number;
  licence_key: string;
  product_key: string;
  release: string;
  vmclient: number;
  vmlicense: string;
  skip: number;
}

export class TelemetryPushInfo {
  email: string;
  company: string;
  application: string;
  build: string;
  expdate: number;
  licensekey: string;
  productkey: string;
  release: string;
  vmclient: number;
  vmlicense: string;
}

export class VirtualMachine extends DCNetscopeResourceWithName {
}

export class HostSystem extends DCNetscopeResourceWithName {
  children: Array<string>;
}

export class Network extends DCNetscopeResourceWithName {
}

export class DistributedVSwitch extends DCNetscopeResourceWithName {
  children: Array<PortGroup>;
}

export class PortGroup extends DCNetscopeResourceWithName {
}

export class DuckDbResourceObject {
  uuid: string;
  type: string;
  name: string;
  address: string;
  host?: string;
  vms?: string[];
}

export class DuckDbExternalIp {
  uuid: string;
  dcnetscope_uuid: string;
  ipAddress: string;
  name: string;
}

export class DuckDbAppsGroup {
  name: string;
  // Apps groups file holding the group: two files can hold groups of the same name
  file?: string;
  vms: {
    uuid: string
  }[];
}

const APPS_GROUP_REFERENCE_PREFIX = "appgroup:";

/**
 * Resource of the filter of the top bar standing for an apps group: its uuid names the file and the group
 * ('appgroup:<file>/<name>'), and its name shows the file next to the group.
 */
export function appsGroupFilterResource(appGroup: DuckDbAppsGroup): DCNetscopeResourceWithName {
  if (appGroup.file === undefined) {
    return new DCNetscopeResourceWithName(appGroup.name, appGroup.name);
  }
  return new DCNetscopeResourceWithName(`${APPS_GROUP_REFERENCE_PREFIX}${appGroup.file}/${appGroup.name}`,
    `${appGroup.name} (${appGroup.file})`);
}

/**
 * File and name of the apps group a filter stands for. A file name holds no '/', a group name can (a network). A
 * filter written before the groups were told apart by their file, such as a link shared then, only names the group:
 * it matches the groups of this name in any file.
 */
export function appsGroupFilterReference(uuid: string): {file?: string, name: string} {
  if (!uuid.startsWith(APPS_GROUP_REFERENCE_PREFIX)) {
    return {name: uuid};
  }
  let reference = uuid.substring(APPS_GROUP_REFERENCE_PREFIX.length);
  let separator = reference.indexOf("/");
  return {file: reference.substring(0, separator), name: reference.substring(separator + 1)};
}


export class DuckDbProtocolRatioDescription {
  protocol: string
  protocol_total_bytes: number
}

export class DuckDbEmitterDescription {
  src_resource_uuid: string;
  src_ipaddr: string;
  source_total_bytes: number;
  name?: string
}

export class DuckDbFlowDescription {
  src_ipaddr: string;
  dst_ipaddr: string;
  src_resource_type: string;
  src_resource_uuid: string;
  dst_resource_uuid: string;
  dst_resource_type: string;
  exchanged_packets: number;
  exchanged_bytes: number;
  source?: DuckDbResourceObject;
  target?: DuckDbResourceObject;
  src_object?: DuckDbResourceObject;
  dst_object?: DuckDbResourceObject;
  port?: number;
  protocol?: string;
  proto_raw?: string;
  direction?: number;
  src_country?: string;
  src_country_code?: string;
  src_asn?: string;
  src_asn_num?: number;
  dst_country?: string;
  dst_country_code?: string;
  dst_asn_num?: number;
  dst_asn?: string;
}

export class DuckDbDashboardData {
  allFlowsRepository: FlowsRepository;
  flowsRepository: FlowsRepository;
  // totalPacketsResults: number;
  // totalBytesResults: number;
  // protocolRatio: DuckDbProtocolRatioDescription[];
  // topEmitters: DuckDbEmitterDescription[];
  // nodeMap: Map<string, FlowRessourceObject>;
  // allFlows: FlowDetail[];
  // allFlowsWithPortsAndProtocols: FlowDetail[];
  nodeMapping: Map<string, DuckDbResourceObject>;
  externalIps: Array<DuckDbExternalIp>;
  appsGroups: Array<DuckDbAppsGroup>;
  selectedTimestamps: [number, number];
  timeSelectionLabel: string;
  // index: {
  //   ips: { address: string }[];
  //   protocols: { protocol: string }[];
  // };
  dashboardDataUuid: string;
  dataUuid: string;
  dataUuidAndWhere: string;
  // threatsFlows: FlowDetail[];
}

export class ClusterFlowDetails {
  dataUuid: string;
  flows: FlowDetail[]
}

export class InfrastructureVersion {
  id: number;
  start_time: number;
  topology: {
    node_mapping: Map<string, VirtualMachine | HostSystem>;
    complete_topology: Array<DatacenterTopologyTreeNode>;
    vm_only_topology: {
      vms: Array<VirtualMachine>;
      hosts: Array<HostSystem>;
      networks: Array<Network>;
      dswitches: Array<DistributedVSwitch>;
      portgroups: Array<PortGroup>;
    }
  }
}

export class DomainResolution {
  status: string;
  result: string;
  geoip?: {
    'ip_version': number,
    'start_ip': string,
    'end_ip': string,
    'country_code': string,
    'country': string,
    'latitude': number,
    'longitude': number,
    'accuracy': number,
    'ip_range': string,
    "asns": [{
        'range_start': string,
        'range_end': string,
        'asn_number': number,
        'country_code': number,
        "asn_description": string,
    }]
  }
}

export class AppsGroupsMetadata {
  uuid?: string;
}

export class AppsGroups {
  metadata: AppsGroupsMetadata;
  uuid: string;
  name: string;
  // Free text written by the user, saved with the group
  description?: string;
  vms: {
    uuid: string;
    name?: string;
    visibility?: boolean;
  }[];
  // Other apps groups whose members belong to this group as well. The reference is kept by name, so that a
  // group can be included in several others without its members being copied around, and by file when the
  // group it points to belongs to another apps groups file. No file means the file holding this group.
  included_apps_groups?: { name: string, file?: string }[];
  // Patterns on the names of the vms and hosts: the ones matching an 'include' pattern and no 'exclude' one are
  // members of the group as well. The appliance writes them in 'matched_vms', when the file is saved and as the
  // inventory changes: they are never saved from the UI.
  members_rules?: { include?: string[], exclude?: string[] };
  matched_vms?: { uuid: string }[];
  // Members each included group brought in, filled once the inclusions are resolved and never saved: 'vms'
  // then holds every member of the group, and the renderer reads these back to draw an included group as a
  // box of its own inside this one. A member the group lists itself is in none of them.
  resolvedInclusions?: ResolvedAppsGroupsInclusion[];
  jointJsObject?: Object;
  mode?: AppsGroupMembersDisplayMode = "summarized";
}

/**
 * Box of an apps group included in another one, as the renderer last drew it: the box and the panel both read
 * it back to open or close the box.
 */
export class IncludedAppsGroupBoxState {
  // Key of the inclusion among the ones of the including group (see includedAppsGroupKey)
  key: string;
  // Name of the apps group including this one, whose display state keeps whether it is opened
  includingAppsGroupName: string;
  // Opened by the user, or because one of its members is shown
  expanded: boolean;
  // Resources the box stands for, the networks of the included group resolved
  memberUuids: string[];
  // Members as the included group lists them, its networks included
  declaredMemberUuids: string[];
  // The same, without the ones held by the boxes of the groups it includes in turn: opening the box shows these,
  // and the boxes of its own included groups closed
  ownMemberUuids?: string[];
  ownDeclaredMemberUuids?: string[];
  // Apps group the box stands for, and the file holding it, where its settings are
  name?: string;
  file?: string;
  // Number of apps groups it includes in turn, drawn as boxes inside of it
  includedCount?: number;
}

export class ResolvedAppsGroupsInclusion {
  name: string;
  // No file means the file holding the group that carries the inclusion
  file?: string;
  members: { uuid: string; name?: string; }[];
  // The groups the included group includes in turn, among the members above, at any depth. Their file is
  // always set.
  inclusions?: ResolvedAppsGroupsInclusion[];
}

export class AppsGroupsData {
  apps_groups: AppsGroups[];
}

export class ReportsData {
  reports: {
    flow_run_uuid: string;
    generation_result?: {
      pdf_path?: string;
      // Path of the reports that are not pdfs
      file_path?: string;
      status: string;
    }
    params: {
      start_date: string;
      end_date: string;
      report_type?: string;
      apps_groups_files?: string[];
    }
    state: string;
    uuid: string;
    created_at: number;
    launched_at?: number;
    finished_at?: number;
    elapsed_time: number;
  }[];
}

export class DownloadReportData {
  result: number[];
  filename: string
}

export class ASN {
  name: string;
}

export class Country {
  name: string;
  code: string;
}

export class IpDescription {
  ip_version: string;
  start_ip: string;
  end_ip: string;
  country_code: string;
  country: string;
  latitude: number;
  longitude: number;
  accuracy: number;
  ip_range: string;
  asns: {
    range_start: string;
    range_end: string;
    asn_number: number
    country_code: string;
    asn_description: string;
  }[]
}

export class SourceOrDestinationDescription {
  source?: IpDescription;
  destination?: IpDescription;
}

export class VmOpenPortDescription {
  description: {
    name: string;
    uuid: string;
  }
  ports: Map<number, string[]>;
}

export type TroubleshootingData = {
  all_ips: [string, string, string, string, number, number, SourceOrDestinationDescription][];
  vms_with_accessible_ports: VmOpenPortDescription[];
  params: {
    start_time: string;
    end_time: string;
  }
};

export class TemporalParameter {

  constructor(startTime, endTime, timeResolution, timeSelection: string = "unknown") {
    this.startTime = startTime;
    this.endTime = endTime;
    this.timeResolution = timeResolution;
    this.timeSelection = timeSelection;
  }

  startTime: any;
  endTime: any;
  timeResolution: string;
  timeSelection: string;
}

function getShortUuid(o: DCNetscopeResourceWithName): string {
  if (o.uuid.indexOf(":") != -1) {
    let uuidParts = o.uuid.split(":");
    if (uuidParts.length >= 2) {
      return uuidParts[1];
    }
  }
  return this.uuid;
}

export class ComboboxDataSourceHelper {
  nodeMapping: Map<string, DuckDbResourceObject>;
  externalIps: Array<DuckDbExternalIp>;
  appsGroups: Array<DuckDbAppsGroup>;
}

export class ComputingResourceCollection<T extends DCNetscopeResourceWithName> extends Array<T> {
  generateShortUuids(): Array<string> {
    return this.map((resource) => getShortUuid(resource));
  }
}

export class SearchableComboboxSelectionItem {
  id: number;
  item: ComboboxSelectionItem;
}

export class ComboboxSelectionItem {
  resource: DCNetscopeResourceWithName;
  mode: string = "focus";
  manualKey: string = undefined;
  manualSqlWhereClause: string;
  manualSqlUuids: string[] = [];

  constructor(resource: DCNetscopeResourceWithName, mode: string) {
    this.resource = resource;
    this.mode = mode;
  }

  initManualSqlWhereClause = (data: ComboboxDataSourceHelper) => {
    let result = "true";  // No filtering if the appgroup is not found
    if (this.mode == "appgroup") {
      let reference = appsGroupFilterReference(this.resource.uuid);
      let matchingAppGroups = data.appsGroups.filter((appGroup) =>
        appGroup.name === reference.name && (reference.file === undefined || appGroup.file === reference.file));
      if (matchingAppGroups.length > 0) {
        // A filter naming no file matches the groups of its name in every file
        this.manualSqlUuids = Array.from(new Set(matchingAppGroups
          .flatMap((matchingAppGroup) => (matchingAppGroup.vms ?? []).map((vm) => vm.uuid))));
        let expressions = this.manualSqlUuids.map((vmUuid) => `(src_resource_uuid = '${vmUuid}' or dst_resource_uuid = '${vmUuid}')`);
        // An apps group without any member matches no flow: an empty clause would break the query
        let completeClause = expressions.length > 0 ? expressions.join(" OR ") : "false";
        result = completeClause;
      }
    }

    if (this.resource.uuid.startsWith("ip:") || this.resource.uuid.startsWith("network:")) {
      let resourceType = this.resource.uuid.split(":")[0];
      let itemName = this.resource.name;
      if (resourceType == "ip") {
        result = ` ((src_ipaddr = '${itemName}') or (dst_ipaddr = '${itemName}')) `;
      }
      if (resourceType == "network") {
        result = ` ((src_ipaddr::INET) <<= (INET '${itemName}') or (dst_ipaddr::INET) <<= (INET '${itemName}')) `;
      }
    }

    this.manualSqlWhereClause = result;
  }

  get key(): string {
    if (this.manualKey === undefined) {
      if (this.resource.name === undefined && this.resource.address !== undefined) {
        return `${this.mode} ${this.resource.address}`;
      }
      return `${this.mode} ${this.resource.name}`;
    } else {
      return this.manualKey;
    }
  }

  getSqlClause(): string {
    let clauses = [];
    let joinOperator = "or";

    let clauseOperator = "=";
    if (this.mode === "ignore") {
      clauseOperator = "!="
    }

    if (this.mode == "focus" || this.mode == "source" || this.mode == "ignore") {
      if (this.manualSqlWhereClause != undefined) {
        clauses.push(this.manualSqlWhereClause);
      } else {
        let newClause = this.resource.type === "unknown_ip" ? `src_ipaddr ${clauseOperator} '${this.resource.address}'` : `src_resource_uuid ${clauseOperator} '${this.resource.uuid}'`;
        clauses.push(newClause);
      }
    }
    if (this.mode == "focus" || this.mode == "destination" || this.mode == "ignore") {
      let newClause = this.resource.type === "unknown_ip" ? `dst_ipaddr ${clauseOperator} '${this.resource.address}'` : `dst_resource_uuid ${clauseOperator} '${this.resource.uuid}'`;
      clauses.push(newClause);
    }
    if (this.mode == "ignore"){
      joinOperator = "and";
    }
    if (this.mode == "protocol") {
      let newClause = `protocol = '${this.resource.name}'`;
      clauses.push(newClause)
    }
    if (this.mode == "appgroup") {
      if (this.manualSqlWhereClause !== undefined) {
        clauses.push(this.manualSqlWhereClause);
      } else {
        console.warn("Missing clause for this ComboboxSelectionItem");
        console.warn(this);
      }
    }

    if (clauses.length == 0) {
      return "true";
    } else {
      let result = clauses.map((clause) => `(${clause})`).join(` ${joinOperator} `);
      return result;
    }
  }
}

/**
 * SQL clause matching the flows the filters of the top bar keep. The filters of a same kind add up: the resources to
 * focus on (as a source, a destination or both), the protocols, the apps groups. The kinds narrow each other. The
 * resources to ignore are removed from what the other filters keep, each of them: they never add up, as 'ignore A' or
 * 'ignore B' would only remove the flows between A and B.
 */
export function comboboxSelectionWhereClause(selection: ComboboxSelectionItem[], data: ComboboxDataSourceHelper): string {
  let anyOf = (items: ComboboxSelectionItem[]) => items.length == 0
    ? "true"
    : items.map((item) => `(${item.getSqlClause()})`).join(" or ");
  let allOf = (items: ComboboxSelectionItem[]) => items.length == 0
    ? "true"
    : items.map((item) => `(${item.getSqlClause()})`).join(" and ");

  let appsGroups = selection.filter((item) => item.mode === "appgroup");
  // The members of the apps groups are only known from the data being displayed
  appsGroups.forEach((item) => item.initManualSqlWhereClause(data));

  let focusedResources = selection.filter((item) => ["focus", "source", "destination"].includes(item.mode));
  let ignoredResources = selection.filter((item) => item.mode === "ignore");
  let protocols = selection.filter((item) => item.mode === "protocol");

  return [anyOf(focusedResources), allOf(ignoredResources), anyOf(protocols), anyOf(appsGroups)]
    .map((clause) => `(${clause})`)
    .join(" and ");
}

export class HeatmapFlowDetail {
  flows: DuckDbFlowDescription[];
  node_mapping: Map<string, DuckDbResourceObject>;
  countries: Array<[string, string]>;
  asns: Array<[string, Number]>;
}

export class ResourceFlowDescriptionRule {
  name: string;
  uuid: string;
}

/**
 * Kinds of the ends a network rule accepts as its source or its destination:
 *  - 'resource': a resource of the infrastructure, by uuid,
 *  - 'apps_group': the members of an apps group, by name and file, resolved each time the rule is evaluated so
 *    that the rule follows the group as it changes,
 *  - 'network': an address, or a network in CIDR notation,
 *  - 'resource_type': every resource of a type (see FLOW_RESOURCE_TYPES), as the flows classify their ends.
 */
export type FlowDescriptionEndpointKind = "resource" | "apps_group" | "network" | "resource_type";

/**
 * Types the flows give to their ends (src_resource_type and dst_resource_type columns), with their labels
 */
export const FLOW_RESOURCE_TYPES: {type: string, label: string}[] = [
  {type: "vm", label: "VMs"},
  {type: "host", label: "Hosts"},
  {type: "external_ip", label: "External IPs"},
  {type: "internet_ip", label: "Internet IPs"},
  {type: "unknown_ip", label: "Unknown IPs"},
];

export class FlowDescriptionEndpoint {
  kind: FlowDescriptionEndpointKind;
  // Uuid of a resource
  uuid?: string;
  // Name of a resource, kept to label it, or name of an apps group
  name?: string;
  // Apps groups file holding an apps group
  file?: string;
  // Address or network in CIDR notation
  address?: string;
  // Type of the resources, one of FLOW_RESOURCE_TYPES
  resourceType?: string;

  static resource(uuid: string, name: string): FlowDescriptionEndpoint {
    return Object.assign(new FlowDescriptionEndpoint(), {kind: "resource", uuid: uuid, name: name});
  }

  static appsGroup(name: string, file: string): FlowDescriptionEndpoint {
    return Object.assign(new FlowDescriptionEndpoint(), {kind: "apps_group", name: name, file: file});
  }

  static network(address: string): FlowDescriptionEndpoint {
    return Object.assign(new FlowDescriptionEndpoint(), {kind: "network", address: address});
  }

  static resourceType(resourceType: string): FlowDescriptionEndpoint {
    return Object.assign(new FlowDescriptionEndpoint(), {kind: "resource_type", resourceType: resourceType});
  }

  static from(object): FlowDescriptionEndpoint {
    if (object?.kind == "apps_group") {
      return FlowDescriptionEndpoint.appsGroup(object.name, object.file);
    }
    if (object?.kind == "network") {
      return FlowDescriptionEndpoint.network(object.address);
    }
    if (object?.kind == "resource_type") {
      return FlowDescriptionEndpoint.resourceType(object.resourceType);
    }
    return FlowDescriptionEndpoint.resource(object?.uuid, object?.name);
  }

  // Label of the end, an apps group being written with its file as two files may hold groups of the same name
  label(): string {
    if (this.kind == "apps_group") {
      return `${this.name} (${this.file})`;
    }
    if (this.kind == "network") {
      return this.address;
    }
    if (this.kind == "resource_type") {
      return FLOW_RESOURCE_TYPES.find((type) => type.type == this.resourceType)?.label ?? this.resourceType;
    }
    return this.name ?? this.uuid;
  }
}

/**
 * Members an apps group stands for once its inclusions are followed. An apps group that could not be found (its
 * file was deleted, or the group renamed or removed) stands for nothing: a rule pointing to it matches no flow,
 * rather than silently matching every flow.
 */
export class ResolvedAppsGroup {
  found: boolean = false;
  members: {uuid: string, name?: string}[] = [];
}

export interface AppsGroupsReferenceResolver {
  resolve(name: string, file: string): ResolvedAppsGroup;
}

// The side of a network rule, the source or the destination
export type FlowDescriptionSide = "source" | "destination";

// 'any' matches every end, 'specific' only the ends listed, none at all when the list is empty
export type FlowDescriptionEndpointsMode = "any" | "specific";

export class FlowDescriptionNetworkRule {
  sourceMode: FlowDescriptionEndpointsMode;
  sources: FlowDescriptionEndpoint[] = [];

  destinationMode: FlowDescriptionEndpointsMode;
  destinations: FlowDescriptionEndpoint[] = [];

  protocolMode : "any" | "specific";

  portMode : "any" | "custom";

  port: string;
  protocol: string;
  direction: string;
  twoWays: boolean;

  constructor(
    sourceMode: FlowDescriptionEndpointsMode, sources: FlowDescriptionEndpoint[],
    destinationMode: FlowDescriptionEndpointsMode, destinations: FlowDescriptionEndpoint[],
    protocolMode : "any" | "specific", portMode : "any" | "custom",
    port: string, protocol: string, direction: string, twoWays: boolean
  ) {
    this.sourceMode = sourceMode;
    this.sources = sources;

    this.destinationMode = destinationMode;
    this.destinations = destinations;

    this.protocolMode = protocolMode;
    this.portMode = portMode;

    this.port = port;
    this.protocol = protocol;
    this.direction = direction;

    this.twoWays = twoWays;
  }

  static createDefault() {
    return new FlowDescriptionNetworkRule(
      "any", [],
      "any", [],
      "any", "any",
      "80", "", "0",
      false
    );
  }

  // Builds a network rule from a plain object. The object can either be a network rule, or a legacy flow
  // description, as descriptions used to embed the network aspect (source, destination, protocol, port,
  // direction) directly.
  static from(object) {
    let [sourceMode, sources] = FlowDescriptionNetworkRule.endpointsFrom(object, "source");
    let [destinationMode, destinations] = FlowDescriptionNetworkRule.endpointsFrom(object, "destination");

    let result = new FlowDescriptionNetworkRule(
      sourceMode, sources,
      destinationMode, destinations,

      object.protocolMode ?? "any",
      object.portMode ?? "any",

      object.port ?? "",
      object.protocol ?? "",
      object.direction ?? "n/a",
      object.twoWays ?? false
    );

    return result;
  }

  /**
   * Reads one side of a rule. Rules used to accept a single kind of end per side, a list of resources or one
   * network ('sourceMode' being 'resources' or 'network_address'): they are turned into a list of ends, which
   * is what they are written back as.
   */
  private static endpointsFrom(object, side: FlowDescriptionSide): [FlowDescriptionEndpointsMode, FlowDescriptionEndpoint[]] {
    let mode = object[`${side}Mode`] ?? "any";
    let endpoints = object[`${side}s`];

    if (endpoints != undefined) {
      return [mode == "any" ? "any" : "specific", endpoints.map((endpoint) => FlowDescriptionEndpoint.from(endpoint))];
    }

    if (mode == "resources") {
      let resources = (object[`${side}Resources`] ?? [])
        .map((resource) => FlowDescriptionEndpoint.resource(resource.uuid, resource.name));
      return ["specific", resources];
    }

    if (mode == "network_address") {
      let address = (object[`${side}NetworkAddress`] ?? "").trim();
      return ["specific", address == "" ? [] : [FlowDescriptionEndpoint.network(address)]];
    }

    return ["any", []];
  }

  modeOf = (side: FlowDescriptionSide): FlowDescriptionEndpointsMode => {
    return side == "source" ? this.sourceMode : this.destinationMode;
  }

  endpointsOf = (side: FlowDescriptionSide): FlowDescriptionEndpoint[] => {
    return side == "source" ? this.sources : this.destinations;
  }

  setEndpoints = (side: FlowDescriptionSide, endpoints: FlowDescriptionEndpoint[]) => {
    if (side == "source") {
      this.sources = endpoints;
    } else {
      this.destinations = endpoints;
    }
  }

  // Apps groups the rule points to, which have to be loaded before the rule is evaluated
  appsGroupsReferences = (): {name: string, file: string}[] => {
    return this.sources.concat(this.destinations)
      .filter((endpoint) => endpoint.kind == "apps_group")
      .map((endpoint) => ({name: endpoint.name, file: endpoint.file}));
  }

  endpointsLabel = (side: FlowDescriptionSide) : string => {
    if (this.modeOf(side) == "any") {
      return "any";
    }
    let endpoints = this.endpointsOf(side);
    return endpoints.length == 0 ? "nothing" : endpoints.map((endpoint) => endpoint.label()).join(", ");
  }

  sourceLabel = () : string => {
    return this.endpointsLabel("source");
  }

  destinationLabel = () : string => {
    return this.endpointsLabel("destination");
  }

  protocolLabel = () : string => {
    return this.protocolMode == "any" ? "any" : this.protocol;
  }

  portLabel = () : string => {
    return this.portMode == "any" ? "any" : this.port;
  }

  directionLabel = () : string => {
    if (this.direction == "1") {
      return "port is source side";
    }
    if (this.direction == "0") {
      return "port is destination side";
    }
    return "n/a";
  }

  toSummary = () : string => {
    let arrow = this.twoWays ? "<->" : "->";
    return `${this.sourceLabel()} ${arrow} ${this.destinationLabel()} [protocol: ${this.protocolLabel()}, port: ${this.portLabel()}]`;
  }

  /**
   * SQL clause matching the flows of the rule. The apps groups the rule points to are read from the resolver,
   * which must have loaded them: an apps group it does not know matches no flow.
   */
  toSqlClause = (isOppositeRule: boolean, resolver?: AppsGroupsReferenceResolver): string => {
    let subClauses: string[] = [];

    let srcPrefix = "src";
    let dstPrefix = "dst";

    if (isOppositeRule) {
      srcPrefix = "dst";
      dstPrefix = "src";
    }

    // Source and destination, 'any' adding nothing
    if (this.sourceMode != "any") {
      subClauses.push(FlowDescriptionNetworkRule.endpointsSqlClause(this.sources, srcPrefix, resolver));
    }
    if (this.destinationMode != "any") {
      subClauses.push(FlowDescriptionNetworkRule.endpointsSqlClause(this.destinations, dstPrefix, resolver));
    }

    // Protocol
    if (this.protocolMode == "any") {
      // Do nothing
    }
    if (this.protocolMode == "specific") {
      let protocolClause = `protocol = '${this.protocol}'`;
      subClauses.push(protocolClause);
    }

    // Port
    if (this.portMode == "any") {
      // Do nothing
    }
    if (this.portMode == "custom") {
      let portsExpression: string[] = [];
      let allPortsRawExpressions = this.port.split(",");

      for (let rawExpression of allPortsRawExpressions) {
        let trimedRawExpression = rawExpression.trim();

        let portClause = `false`;
        if (trimedRawExpression.includes("-")) {
          let firstPart = trimedRawExpression.split("-")[0];
          let secondPart = trimedRawExpression.split("-")[1];
          portClause = `dst_port >= ${firstPart} and dst_port <= ${secondPart}`;
        } else {
          portClause = `dst_port = ${trimedRawExpression}`;
        }

        portsExpression.push(portClause)
      }

      if (portsExpression.length > 0) {
        let portClause = "(";
        portClause += portsExpression.join(" or ");
        portClause += ")";

        subClauses.push(portClause);
      }


      // Direction
      if (this.direction != "n/a") {
        let directionAsBoolean = this.direction == "1";
        let directionValue = directionAsBoolean ? 1 : 0;
        if (isOppositeRule) {
          directionValue = directionAsBoolean ? 0 : 1;
        }

        let directionClause = `direction = ${directionValue}`;
        subClauses.push(directionClause);
      }
    }

    let result = "false";
    if (subClauses.length > 0) {
      result = subClauses.join(" AND ");
    }

    return result;
  }

  // A side matches a flow as soon as one of its ends does, and none when it lists no end
  static endpointsSqlClause(endpoints: FlowDescriptionEndpoint[], prefix: string,
                            resolver?: AppsGroupsReferenceResolver): string {
    let clauses = endpoints.map((endpoint) => FlowDescriptionNetworkRule.endpointSqlClause(endpoint, prefix, resolver));
    return clauses.length == 0 ? "false" : `(${clauses.join(" or ")})`;
  }

  static endpointSqlClause(endpoint: FlowDescriptionEndpoint, prefix: string,
                           resolver?: AppsGroupsReferenceResolver): string {
    if (endpoint.kind == "network") {
      return FlowDescriptionNetworkRule.addressSqlClause(endpoint.address, prefix);
    }

    if (endpoint.kind == "resource_type") {
      return `${prefix}_resource_type = '${sqlString(endpoint.resourceType)}'`;
    }

    if (endpoint.kind == "apps_group") {
      let resolved = resolver?.resolve(endpoint.name, endpoint.file);
      if (resolved == undefined || !resolved.found) {
        return "false";
      }
      return FlowDescriptionNetworkRule.membersSqlClause(resolved.members, prefix);
    }

    return `${prefix}_resource_uuid = '${sqlString(endpoint.uuid)}'`;
  }

  static addressSqlClause(address: string, prefix: string): string {
    let cleanedAddress = (address ?? "").trim();

    if (cleanedAddress == "") {
      return "false";
    }
    if (cleanedAddress.includes("/")) {
      return `((${prefix}_ipaddr::INET) <<= (INET '${sqlString(cleanedAddress)}'))`;
    }
    return `${prefix}_ipaddr = '${sqlString(cleanedAddress)}'`;
  }

  /**
   * Clause matching the members of an apps group, as the apps groups view draws them:
   *  - a network typed in CIDR notation matches the addresses falling inside it,
   *  - a configured external IP matches itself, and the addresses DC Netscope named after it when it is a
   *    network ('vim.ExternalIpaddress:bureau:13'),
   *  - any other member matches the resource wearing its uuid.
   */
  static membersSqlClause(members: {uuid: string}[], prefix: string): string {
    let uuids: string[] = [];
    let clauses: string[] = [];

    for (let member of members) {
      if (member.uuid == undefined) {
        continue;
      }

      if (member.uuid.startsWith(IP_NETWORK_UUID_PREFIX)) {
        clauses.push(FlowDescriptionNetworkRule.addressSqlClause(uuidAddress(member.uuid), prefix));
        continue;
      }

      uuids.push(member.uuid);

      let isConfiguredExternalIp = member.uuid.startsWith(EXTERNAL_IP_UUID_PREFIX) && member.uuid.split(":").length == 2;
      if (isConfiguredExternalIp) {
        clauses.push(`starts_with(${prefix}_resource_uuid, '${sqlString(member.uuid)}:')`);
      }
    }

    if (uuids.length > 0) {
      clauses.unshift(`${prefix}_resource_uuid IN (${uuids.map((uuid) => `'${sqlString(uuid)}'`).join(", ")})`);
    }

    return clauses.length == 0 ? "false" : `(${clauses.join(" or ")})`;
  }
}

// A text written inside a SQL string, its quotes doubled
function sqlString(value: string): string {
  return (value ?? "").replace(/'/g, "''");
}

export class FlowDescriptionRule {
  // Data classifications are free labels, these ones are only proposed to the user
  static suggestedDataClassifications: string[] = ["Operational", "Security events", "Personal data"];

  name: string;
  comment: string;
  authorized: boolean;
  risk: "critical" | "medium" | "low";
  dataClassifications: string[] = [];
  networkRules: FlowDescriptionNetworkRule[] = [];

  constructor(name: string, comment: string, authorized: boolean, risk: "critical" | "medium" | "low",
              dataClassifications: string[], networkRules: FlowDescriptionNetworkRule[]) {
    this.name = name;
    this.comment = comment;
    this.authorized = authorized;
    this.risk = risk;
    this.dataClassifications = dataClassifications;
    this.networkRules = networkRules;
  }

  static createDefault() {
    return new FlowDescriptionRule("", "", true, "low", [], []);
  }

  static from(object) {
    // Descriptions used to hold a single network rule inlined in their own fields. Such legacy descriptions are
    // converted into a description owning a single network rule.
    let rawNetworkRules = object.networkRules ?? [object];

    let result = new FlowDescriptionRule(
      object.name,
      object.comment,
      object.authorized,
      // Descriptions saved before the risk and the data classifications were introduced have none
      object.risk ?? "low",
      object.dataClassifications ?? [],
      rawNetworkRules.map((rawNetworkRule) => FlowDescriptionNetworkRule.from(rawNetworkRule))
    );

    return result;
  }

  riskBadgeClass = () : string => {
    let badgeClasses: Map<string, string> = new Map([
      ["critical", "badge badge-danger"],
      ["medium", "badge badge-warning"],
      ["low", "badge badge-success"]
    ]);

    return badgeClasses.get(this.risk) ?? "badge";
  }

  addDataClassification = (dataClassification: string) => {
    let cleanedDataClassification = (dataClassification ?? "").trim();

    if (cleanedDataClassification == "" || this.dataClassifications.includes(cleanedDataClassification)) {
      return;
    }

    this.dataClassifications.push(cleanedDataClassification);
  }

  deleteDataClassification = (dataClassification: string) => {
    this.dataClassifications = this.dataClassifications.filter((d) => d != dataClassification);
  }

  addNetworkRule = () : FlowDescriptionNetworkRule => {
    let networkRule = FlowDescriptionNetworkRule.createDefault();
    this.networkRules.push(networkRule);
    return networkRule;
  }

  deleteNetworkRule = (networkRule: FlowDescriptionNetworkRule) => {
    this.networkRules = this.networkRules.filter((r) => r != networkRule);
  }

  toSqlClause = (resolver?: AppsGroupsReferenceResolver): string => {
    // A description matches a flow as soon as one of its network rules matches it
    let networkRulesSqlClauses = this.networkRules
      .map((r) => ` ( ${r.toSqlClause(false, resolver)} )`);

    let oppositeNetworkRulesSqlClauses = this.networkRules
      .filter((r) => r.twoWays)
      .map((r) => ` ( ${r.toSqlClause(true, resolver)} )`);

    networkRulesSqlClauses.push(...oppositeNetworkRulesSqlClauses);

    if (networkRulesSqlClauses.length == 0) {
      return "false";
    }

    return networkRulesSqlClauses.join(" or ");
  }
}
